import React, { Component } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  InfiniteLoader,
  CellMeasurer,
  CellMeasurerCache,
  Table,
  Column
} from 'react-virtualized'
import { Alert } from 'reactstrap'
import moment from 'moment'
import { withNamespaces } from 'react-i18next'
import styled from 'styled-components'

import {
  fetchTransactions,
  fetchTransactionDetail
} from '../../ducks/transactions'

import { formatHours } from '../../helpers'

const TableWrapper = styled.div`
  max-width: 1100px;
  overflow: auto;
`

const StyledHours = styled.div`
  text-align: center;
`

const StyledCell = styled.div`
  white-space: normal;
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  color: #2e384d;
`

const StyledLink = styled.a``

class TransactionsTable extends Component {
  constructor(props) {
    super(props)

    this._cache = new CellMeasurerCache({
      minHeight: 72,
      fixedWidth: true
    })

    this.gridRef = React.createRef()
  }

  componentDidUpdate() {
    if (!this.props.transactions.error) {
      this.gridRef.current.forceUpdate()
      this.gridRef.current.forceUpdateGrid()
    }
  }

  render() {
    const { t, height } = this.props
    const { entities, isMoreToLoad, error } = this.props.transactions

    if (error) {
      return <Alert color="danger">{error.message}!</Alert>
    }

    return (
      <InfiniteLoader
        isRowLoaded={this.isRowLoaded}
        rowCount={isMoreToLoad ? entities.length + 1 : entities.length}
        loadMoreRows={this.loadMoreRows}
        threshold={2}
      >
        {({ onRowsRendered }) => {
          return (
            <TableWrapper>
              <Table
                ref={this.gridRef}
                deferredMeasurementCache={this._cache}
                onRowsRendered={onRowsRendered}
                headerHeight={20}
                height={height}
                overscanRowCount={2}
                rowHeight={this._cache.rowHeight}
                rowGetter={this._rowGetter}
                rowCount={entities.length}
                width={1100}
              >
                <Column
                  dataKey="date"
                  label={t('transactions:date')}
                  width={120}
                  cellRenderer={this._columnCellRenderer}
                />
                <Column
                  dataKey="hours"
                  label={t('transactions:hours')}
                  width={120}
                  cellRenderer={this._columnCellRenderer}
                />
                <Column
                  dataKey="subject"
                  label={t('transactions:subject')}
                  width={400}
                  cellRenderer={this._columnCellRenderer}
                />
                <Column
                  dataKey="type"
                  label={t('transactions:type')}
                  width={200}
                  cellRenderer={this._columnCellRenderer}
                />
                <Column
                  dataKey="comment"
                  label={t('transactions:comment')}
                  width={300}
                  cellRenderer={this._columnCellRenderer}
                />
              </Table>
            </TableWrapper>
          )
        }}
      </InfiniteLoader>
    )
  }

  _rowGetter = ({ index }) => {
    const { entities } = this.props.transactions

    return entities[index]
  }

  _columnCellRenderer = ({ dataKey, parent, rowIndex }) => {
    const { t } = this.props
    const datum = this._rowGetter({ index: rowIndex })

    let content

    switch (dataKey) {
      case 'date':
        content = moment(datum.insert_date).format('DD.MM.YY HH:mm')
        break
      case 'type':
        content = datum.type ? t(`transactions:types:${datum.type}`) : ''
        break
      case 'comment':
        content = datum.reference
        break
      case 'hours':
        content = <StyledHours>{formatHours(datum.amount)}</StyledHours>
        break
      case 'status':
        content = datum.work_request_status
          ? t(`statuses:${datum.work_request_status}`)
          : ''
        break
      case 'subject':
        if (datum.work_request_num || datum.work_request_subject) {
          content = (
            <StyledLink href="#" onClick={this.openDetailModal(datum)}>
              {datum.work_request_num || ''} {datum.work_request_subject || ''}{' '}
              -{' '}
              {datum.work_request_status
                ? t(`statuses:${datum.work_request_status}`)
                : ''}
            </StyledLink>
          )
        } else {
          content = null
        }
        break

      default:
        content = 'test'
        break
    }

    if (dataKey === 'subject' || dataKey === 'type' || dataKey === 'comment') {
      return (
        <CellMeasurer
          cache={this._cache}
          columnIndex={0}
          key={dataKey}
          parent={parent}
          rowIndex={rowIndex}
        >
          <StyledCell>{content}</StyledCell>
        </CellMeasurer>
      )
    }

    return <StyledCell key={dataKey}>{content}</StyledCell>
  }

  toggleModal = () => {
    this.setState(({ isModalOpen }) => ({ isModalOpen: !isModalOpen }))
  }

  isRowLoaded = ({ index }) => index < this.props.transactions.entities.length

  loadMoreRows = () => {
    if (!this.props.transactions.loading) {
      this.props.fetchTransactions()
    }
  }

  _cellRenderer = ({ columnIndex, key, parent, rowIndex, style }) => {
    if (rowIndex === 0) {
      return this._renderHeaderCell({
        columnIndex,
        parent,
        key,
        rowIndex,
        style
      })
    } else {
      return this._renderBodyCell({ columnIndex, parent, key, rowIndex, style })
    }
  }

  openDetailModal = (data) => (ev) => {
    ev.preventDefault()
    this.props.fetchTransactionDetail(data.work_request_id)
    this.props.toggleModal()
  }
}

export default compose(
  connect(
    ({ transactions }) => ({ transactions }),
    { fetchTransactions, fetchTransactionDetail }
  ),
  withNamespaces()
)(TransactionsTable)
